/**
 * Pure helpers behind usePipelineStore's `persist` config (FTT-16444).
 *
 * The global Created On / Owner filters survive a page refresh, so a DE who
 * widened the range past the 3-month default doesn't have to re-pick it on
 * every reload. Store-free + window-free so it unit-tests under the `node`
 * vitest env and so the store can import it without a cycle.
 */
import type { DateRange } from '../types';
import { DEFAULT_DATE_RANGE_MONTHS } from '../../constants';

/** The rolling default window: the last DEFAULT_DATE_RANGE_MONTHS months up to now. */
export function getDefaultDateRange(): DateRange {
  const now = new Date();
  const from = new Date();
  from.setMonth(now.getMonth() - DEFAULT_DATE_RANGE_MONTHS);
  return {
    from: from.toISOString(),
    to: now.toISOString(),
  };
}

/** Calendar day of an ISO instant ('2026-09-11'), or '' when absent. */
const dayOf = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10) : '');

/**
 * Both ends present and parseable. Two paths write a half/empty range into the
 * store — a notification deep-link (`{from:'', to:''}`) and DateRangeFilter's
 * close-commit (`to: undefined` after a partial pick). Persisting either would
 * pin an unbounded query to the browser permanently, and with no end set the
 * header's clear "×" doesn't render, so there'd be no way back.
 */
export const isValidDateRange = (range: DateRange | null | undefined): boolean =>
  Boolean(range?.from && range?.to) && !Number.isNaN(Date.parse(range!.from)) && !Number.isNaN(Date.parse(range!.to));

/**
 * True when the range is still today's default window. Compared by calendar
 * day because the default seeds `to` with the current clock time.
 */
export const isDefaultDateRange = (range: DateRange | null | undefined): boolean => {
  const defaultRange = getDefaultDateRange();
  return dayOf(range?.from) === dayOf(defaultRange.from) && dayOf(range?.to) === dayOf(defaultRange.to);
};

/**
 * Only a deliberate, usable range is written to storage. Leaving the default
 * out is what keeps the window rolling: the key is absent on rehydrate, so the
 * store's initial `getDefaultDateRange()` — computed for *today* — survives
 * the merge. Persisting it verbatim would freeze the window on whatever day it
 * was first stored.
 */
export const shouldPersistDateRange = (range: DateRange | null | undefined): boolean =>
  isValidDateRange(range) && !isDefaultDateRange(range);

/** The only keys this store round-trips through localStorage. */
export interface PersistedPipelineState {
  ownerFilter: number[];
  dateRange: DateRange;
}

/**
 * Read-side counterpart of `partialize`, so the allowlist is authoritative in
 * both directions: zustand's default merge spreads *whatever* sits under the
 * key over the live state, which would let a hand-edited entry — or keys left
 * behind by a build that persisted more and was then reverted — rehydrate into
 * fields this store means to keep transient. Anything unrecognised is dropped,
 * and a stored range is re-validated rather than trusted.
 */
export function mergePersistedPipelineState<S extends PersistedPipelineState>(persistedState: unknown, current: S): S {
  const persisted = persistedState as Partial<PersistedPipelineState> | null | undefined;
  if (!persisted || typeof persisted !== 'object') return current;

  const merged = { ...current };
  if (Array.isArray(persisted.ownerFilter) && persisted.ownerFilter.every((id) => typeof id === 'number')) {
    merged.ownerFilter = persisted.ownerFilter;
  }
  // `current.dateRange` is the default computed for today — keep it unless the
  // stored range is a usable, deliberately-widened one.
  if (shouldPersistDateRange(persisted.dateRange)) {
    merged.dateRange = persisted.dateRange as DateRange;
  }
  return merged;
}
